/**
 * Pure helpers for the portal rollout screens (tracker item 81): paths,
 * human labels for every status, skip and block reason, the new-campaign
 * form to request conversion, and error messages.
 */
import * as types from '../../api/types';
import { getErrorCode, getErrorMessage } from '../../utils/apiError';

export const PORTAL_ROLLOUT_PATH = '/settings/portal-rollout';
export const PORTAL_ROLLOUT_NEW_PATH = '/settings/portal-rollout/new';
export const CUSTOMER_PORTAL_SETTINGS_PATH = '/settings/customer-portal';
export const ROLLOUT_TEMPLATE_PATH = '/settings/notifications/messages/PORTAL_ROLLOUT_WELCOME';

export function rolloutCampaignPath(id: string): string {
  return `${PORTAL_ROLLOUT_PATH}/${encodeURIComponent(id)}`;
}

export const CAMPAIGN_STATUS_LABELS: Record<types.RolloutCampaignStatus, string> = {
  DRAFT: 'Draft (nothing sent yet)',
  RUNNING: 'Running',
  PAUSED: 'Paused',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export const CAMPAIGN_STATUS_CLASSES: Record<types.RolloutCampaignStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  RUNNING: 'bg-blue-50 text-blue-800',
  PAUSED: 'bg-amber-50 text-amber-900',
  COMPLETED: 'bg-green-50 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-700',
};

export const RECIPIENT_STATUS_LABELS: Record<types.RolloutRecipientStatus, string> = {
  PENDING: 'Waiting to be sent',
  SENT: 'Sent, not activated',
  ACTIVATED: 'Activated',
  SKIPPED: 'Skipped',
  FAILED: 'Failed',
  EXPIRED: 'Link expired',
  BOUNCED: 'Email bounced',
};

export const RECIPIENT_STATUS_CLASSES: Record<types.RolloutRecipientStatus, string> = {
  PENDING: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-50 text-blue-800',
  ACTIVATED: 'bg-green-50 text-green-800',
  SKIPPED: 'bg-gray-100 text-gray-700',
  FAILED: 'bg-red-50 text-red-800',
  EXPIRED: 'bg-amber-50 text-amber-900',
  BOUNCED: 'bg-red-50 text-red-800',
};

export const RECIPIENT_STATUSES: types.RolloutRecipientStatus[] = ['PENDING', 'SENT', 'ACTIVATED', 'EXPIRED', 'BOUNCED', 'FAILED', 'SKIPPED'];
export const CAMPAIGN_STATUSES: types.RolloutCampaignStatus[] = ['DRAFT', 'RUNNING', 'PAUSED', 'COMPLETED', 'CANCELLED'];

/** Why a customer is not invited. Every code the server can return has a label here. */
export const SKIP_REASON_LABELS: Record<string, string> = {
  NO_EMAIL: 'No email address on file',
  INVALID_ADDRESS: 'The email address is not valid',
  EMAIL_INVALID: 'The email address bounced or was reported as spam',
  OPTED_OUT: 'The customer opted out of notifications',
  ALREADY_HAS_LOGIN: 'Already has a portal login',
  ALREADY_INVITED: 'Still waiting in another rollout campaign',
  EMAIL_IN_USE: 'Another user of the company already uses this email address',
  DUPLICATE_EMAIL: 'Another customer with the same email address is invited instead',
  CAMPAIGN_CANCELLED: 'The campaign was cancelled before this invitation went out',
  NOTIFICATIONS_DISABLED: 'Notifications are turned off for the company',
  EVENT_DISABLED: 'The welcome message is turned off',
  NOT_CONFIGURED: 'No verified sending connection',
  CANCELLED: 'The message was cancelled',
};

/** The reasons the preview can report, in the order the server checks them. */
export const PREVIEW_SKIP_REASONS = ['NO_EMAIL', 'INVALID_ADDRESS', 'EMAIL_INVALID', 'OPTED_OUT', 'ALREADY_HAS_LOGIN', 'ALREADY_INVITED', 'EMAIL_IN_USE', 'DUPLICATE_EMAIL'] as const;

export function skipReasonLabel(code: string | null | undefined, serverText?: string | null): string {
  if (!code) return '';
  return SKIP_REASON_LABELS[code] ?? serverText ?? code;
}

/** Why one recipient cannot be sent another invitation right now. */
export const RESEND_BLOCKED_LABELS: Record<string, string> = {
  CAMPAIGN_NOT_ACTIVE: 'The campaign is not started or was cancelled',
  NOT_RESENDABLE: 'Nothing to resend: not sent yet, activated, skipped or bounced',
  ALREADY_QUEUED: 'Already queued for the next batch',
  RESEND_LIMIT: 'The resend limit of this campaign is reached',
  TOO_SOON: 'Sent too recently',
  ALREADY_ACTIVATED: 'Already activated',
  OPTED_OUT: 'The customer opted out of notifications',
  EMAIL_INVALID: 'The email address bounced or was reported as spam',
  HAS_OTHER_LOGIN: 'The customer now has another portal login',
  LOGIN_DISABLED: 'The portal login was disabled',
};

/** Reasons the bulk follow-up left someone out. */
export const NOT_QUEUED_LABELS: Record<string, string> = {
  TOO_SOON: 'sent too recently',
  RESEND_LIMIT: 'reached the resend limit',
  OPTED_OUT: 'opted out of notifications',
  EMAIL_INVALID: 'email bounced',
  HAS_OTHER_LOGIN: 'now have another login',
  LOGIN_DISABLED: 'login disabled',
  ALREADY_QUEUED: 'already queued',
  NOT_RESENDABLE: 'cannot be resent',
};

export function resendBlockedLabel(code: string | null | undefined): string {
  return code ? RESEND_BLOCKED_LABELS[code] ?? code : '';
}

export function notQueuedText(notQueued: Record<string, number>): string {
  return Object.entries(notQueued)
    .filter(([, n]) => n > 0)
    .map(([code, n]) => `${n} ${NOT_QUEUED_LABELS[code] ?? code}`)
    .join(', ');
}

export interface BlockerFix {
  label: string;
  to: string;
  /** The settings page is edited by admins only (office managers are told to ask one) */
  adminOnly: boolean;
}

/** Where a campaign blocker is fixed. */
export function blockerFix(code: string): BlockerFix | null {
  switch (code) {
    case 'PORTAL_DISABLED':
      return { label: 'Settings > Customer portal', to: CUSTOMER_PORTAL_SETTINGS_PATH, adminOnly: true };
    case 'NOTIFICATIONS_DISABLED':
    case 'NOT_CONFIGURED':
      return { label: 'Settings > Notifications', to: '/settings/notifications', adminOnly: true };
    case 'EVENT_DISABLED':
      return { label: 'the welcome message', to: ROLLOUT_TEMPLATE_PATH, adminOnly: true };
    default:
      return null;
  }
}

export const BLOCKER_LABELS: Record<string, string> = {
  PORTAL_DISABLED: 'The customer portal is turned off.',
  NOTIFICATIONS_DISABLED: 'Notifications are turned off for the company.',
  EVENT_DISABLED: 'The welcome message is turned off.',
  NOT_CONFIGURED: 'There is no verified email sending connection.',
};

export function blockerLabel(code: string, serverText?: string | null): string {
  return BLOCKER_LABELS[code] ?? serverText ?? code;
}

// ------------------------------------------------------------------ form

export const CUSTOMER_STATUS_OPTIONS: { value: types.RolloutCustomerStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PROSPECT', label: 'Prospect' },
  { value: 'LEAD', label: 'Lead' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'LOST', label: 'Lost' },
];

export interface CampaignForm {
  name: string;
  statuses: types.RolloutCustomerStatus[];
  importedOnly: boolean;
  /** Free text, one customer id per line or comma separated; blank = everyone matching */
  customerIds: string;
  limit: string;
  batchSize: string;
  batchGapMinutes: string;
  minResendDays: string;
  maxResends: string;
}

export const DEFAULT_CAMPAIGN_FORM: CampaignForm = {
  name: '',
  statuses: ['ACTIVE'],
  importedOnly: false,
  customerIds: '',
  limit: '',
  batchSize: '50',
  batchGapMinutes: '5',
  minResendDays: '3',
  maxResends: '3',
};

function whole(value: string, label: string, min: number, max: number): number | string {
  const n = Number(value);
  return value.trim() !== '' && /^\d+$/.test(value.trim()) && n >= min && n <= max ? n : `${label}: enter a whole number from ${min} to ${max}.`;
}

/** The request for a form; a text problem comes back instead of a request. */
export function campaignInput(form: CampaignForm, requireName: boolean): { input: types.RolloutCampaignInput | null; error: string | null } {
  const name = form.name.trim();
  if (requireName && (name.length < 1 || name.length > 120)) return { input: null, error: 'Name the campaign (up to 120 characters).' };
  if (form.statuses.length === 0) return { input: null, error: 'Pick at least one customer status.' };
  const batchSize = whole(form.batchSize, 'Emails per batch', 1, 200);
  const gap = whole(form.batchGapMinutes, 'Minutes between batches', 0, 1440);
  const minDays = whole(form.minResendDays, 'Days before a follow-up', 0, 90);
  const maxResends = whole(form.maxResends, 'Most follow-ups per customer', 0, 10);
  let limit: number | string | undefined;
  if (form.limit.trim() !== '') limit = whole(form.limit, 'Limit', 1, 20_000);
  for (const value of [batchSize, gap, minDays, maxResends, limit]) if (typeof value === 'string') return { input: null, error: value };
  const ids = form.customerIds.split(/[\s,;]+/).map((id) => id.trim()).filter(Boolean);
  if (ids.length > 5000) return { input: null, error: 'Pick at most 5,000 customers by id.' };
  const filters: types.RolloutFilters = { customerStatuses: form.statuses, importedOnly: form.importedOnly };
  if (ids.length > 0) filters.customerIds = Array.from(new Set(ids));
  if (typeof limit === 'number') filters.limit = limit;
  return {
    input: {
      ...(requireName ? { name } : {}),
      filters,
      batchSize: batchSize as number,
      batchGapMinutes: gap as number,
      minResendDays: minDays as number,
      maxResends: maxResends as number,
    },
    error: null,
  };
}

// ------------------------------------------------------------------ text

export function dateTimeText(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function dateText(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

/** 0.4 -> "40%", 0.667 -> "66.7%"; an em dash until something was sent. */
export function activationRateText(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const percent = Math.round(value * 1000) / 10;
  return `${Number.isInteger(percent) ? percent.toFixed(0) : percent.toFixed(1)}%`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

/** "about 2 hours", "about 25 minutes", "a few minutes" for the preview's time estimate. */
export function durationText(minutes: number): string {
  if (minutes <= 0) return 'under a minute';
  if (minutes < 60) return plural(minutes, 'minute');
  const hours = Math.round((minutes / 60) * 10) / 10;
  if (hours < 48) return plural(hours, 'hour');
  return plural(Math.round(hours / 24), 'day');
}

/** The next batch line for a running campaign. */
export function nextBatchText(campaign: types.RolloutCampaign): string | null {
  if (campaign.status !== 'RUNNING' || campaign.blockedReason || !campaign.nextBatchAt) return null;
  return `Next batch of up to ${campaign.batchSize} at ${dateTimeText(campaign.nextBatchAt)}.`;
}

/** What the campaign is doing, in one sentence for the detail page. */
export function campaignSummaryText(campaign: types.RolloutCampaign): string {
  const c = campaign.counts;
  switch (campaign.status) {
    case 'DRAFT':
      return `${plural(c.pending, 'customer')} will be invited when you start the campaign.`;
    case 'RUNNING':
      return campaign.blockedReason ? 'Paused automatically until the problem below is fixed.' : `${plural(c.pending + c.resendQueued, 'invitation')} still to send.`;
    case 'PAUSED':
      return `Paused. ${plural(c.pending + c.resendQueued, 'invitation')} still to send.`;
    case 'COMPLETED':
      return 'Every invitation has been sent.';
    default:
      return 'Cancelled. Invitations already sent stay valid until they expire.';
  }
}

/** The text of a failed rollout call; the backend's message wins, blockers get a pointer. */
export function rolloutErrorText(cause: unknown, fallback: string): string {
  const code = getErrorCode(cause);
  const message = getErrorMessage(cause, fallback);
  if (code && BLOCKER_LABELS[code]) return `${message.replace(/\.$/, '')}. Fix this first, then try again.`;
  return message;
}

/** Whether a failed call means "the campaign changed under you": reload it. */
export function isStateConflict(cause: unknown): boolean {
  const code = getErrorCode(cause);
  return code === 'INVALID_STATE' || code === 'NOTHING_TO_SEND' || code === 'NOTHING_TO_RESEND';
}
